import type { FC } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCountryAutocomplete } from '@components/moleculs/autocomplete/hooks/use-country-autocomplete';
import { FormUploadField } from '@components/organisms/forms';
import { FormAutocomplete } from '@components/organisms/forms/form-autocomplete';
import { FormDatePicker } from '@components/organisms/forms/form-date-picker';
import { FormTextField } from '@components/organisms/forms/form-text-field';
import { FORM_MAX_LENGTH } from '@constants';
import { useCountryOptions, useGenderOptions } from '@hooks/options';
import { ManualFieldNamesEnum } from '@models/enums';
import {
    KYB_FIELD_MIME_TYPES,
    MB_8,
} from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/constants';
import { usePersonalData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/hooks/use-personal-data';
import type { MainFieldProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/main-field/types';
import { getManualFieldName } from '@utils/verification/util';
import dayjs from 'dayjs';

// eslint-disable-next-line complexity
export const MainField: FC<MainFieldProps> = ({ field }) => {
    const { watch } = useFormContext();
    const formValues = watch();
    const { data: personalDataFormValues } = usePersonalData();
    const { t } = useTranslation();
    let { label, placeholder } = getManualFieldName(field?.name);
    const genderOptions = useGenderOptions();
    const countryOptions = useCountryOptions();
    const { renderFlagOption, renderInputFlag } = useCountryAutocomplete();

    label = t(label);
    placeholder = t(placeholder);

    let maxLength: number;

    switch (field.name) {
        case ManualFieldNamesEnum.PHONE:
            maxLength = FORM_MAX_LENGTH.PHONE_NUMBER;
            break;
        case ManualFieldNamesEnum.SSN:
            maxLength = FORM_MAX_LENGTH.SSN;
            break;
        case ManualFieldNamesEnum.STATE:
            maxLength = FORM_MAX_LENGTH.STATE;
            break;
        case ManualFieldNamesEnum.ZIP:
            maxLength = FORM_MAX_LENGTH.ZIP;
            break;
        default:
            maxLength = FORM_MAX_LENGTH.DEFAULT;
    }

    switch (field.name) {
        case ManualFieldNamesEnum.FULL_NAME:
        case ManualFieldNamesEnum.EMAIL:
        case ManualFieldNamesEnum.PHONE:
        case ManualFieldNamesEnum.STATE:
        case ManualFieldNamesEnum.CITY:
        case ManualFieldNamesEnum.ADDRESS_LINE1:
        case ManualFieldNamesEnum.ADDRESS_LINE2:
        case ManualFieldNamesEnum.SSN:
        case ManualFieldNamesEnum.ZIP:
        case ManualFieldNamesEnum.ADDRESS: {
            return (
                <FormTextField
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    textFieldProps={{
                        inputProps: { maxLength },
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                        placeholder,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.DOB: {
            return (
                <FormDatePicker
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    datePickerProps={{
                        disabledDate: (date?: Date) => dayjs(date).isAfter(new Date()),
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.COUNTRY:
        case ManualFieldNamesEnum.CITIZENSHIP: {
            return (
                <FormAutocomplete
                    name={field.name}
                    options={countryOptions}
                    autocompleteProps={{
                        renderOption: renderFlagOption,
                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        InputProps: {
                            startAdornment: renderInputFlag(formValues[field.name]?.value),
                        },
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.GENDER: {
            return (
                <FormAutocomplete
                    name={field.name}
                    options={genderOptions}
                    autocompleteProps={{
                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                        disableClearable: true,
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.INCORPORATION_CERTIFICATE:
        case ManualFieldNamesEnum.OWNERSHIP_DOCUMENT: {
            const textFieldProps = {
                fullWidth: true,
                hint: field.meta?.caption,
                helperTextStatic: true,
            };

            return (
                <FormUploadField
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    uploadProps={{
                        maxSize: MB_8,
                        mimeTypes: KYB_FIELD_MIME_TYPES,
                    }}
                    textFieldProps={textFieldProps}
                />
            );
        }
    }

    return <React.Fragment />;
};
